import { useEffect, useId, useRef, useState, type CSSProperties } from "react";
import { cn } from "@/lib/utils";
import type { HeroBeforeAfterCopy } from "@/components/hero/heroSlideCopy";

const SWEEP_FROM = 88;
const SWEEP_TO = 14;
const SWEEP_DELAY_MS = 700;

interface HeroBeforeAfterProps {
  copy: HeroBeforeAfterCopy;
  active: boolean;
  reducedMotion: boolean;
  /** Called when the visitor drags the divider themselves. */
  onInteract: () => void;
}

/**
 * Paper-and-WhatsApp chaos on one side, the same week in Bidmio on the other.
 * Each time the slide comes up, the divider sweeps once from chaos to order;
 * after that it is the visitor's to drag.
 */
export function HeroBeforeAfter({ copy, active, reducedMotion, onInteract }: HeroBeforeAfterProps) {
  const [pos, setPos] = useState(SWEEP_TO);
  const [sweeping, setSweeping] = useState(false);
  const swept = useRef(false);
  const inputId = useId();

  useEffect(() => {
    if (!active) {
      swept.current = false;
      return;
    }
    if (swept.current) return;
    swept.current = true;
    setSweeping(false);
    if (reducedMotion) {
      setPos(SWEEP_TO);
      return;
    }
    setPos(SWEEP_FROM);
    const timer = window.setTimeout(() => {
      setSweeping(true);
      setPos(SWEEP_TO);
    }, SWEEP_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [active, reducedMotion]);

  const style = { "--hero-ba-pos": `${pos}%` } as CSSProperties;

  return (
    <div className="hero-ba-wrap" data-testid="hero-before-after">
      <div className={cn("hero-ba", sweeping && "is-sweeping")} style={style}>
        <div className="hero-ba-before">
          <span className="hero-ba-label">{copy.before}</span>
          {copy.mess.map((note, index) => (
            <p key={note} className={`hero-ba-note hero-ba-note-${index + 1}`}>
              {note}
            </p>
          ))}
        </div>

        <div className="hero-ba-after">
          <span className="hero-ba-label">{copy.after}</span>
          <ul>
            {copy.jobs.map((job) => (
              <li key={job.name}>
                <span>{job.name}</span>
                <span className={`hero-ba-pill is-${job.tone}`}>{job.status}</span>
              </li>
            ))}
          </ul>
          <dl>
            {copy.kpis.map((kpi) => (
              <div key={kpi.label}>
                <dt>{kpi.label}</dt>
                <dd>{kpi.value}</dd>
              </div>
            ))}
          </dl>
        </div>

        <span className="hero-ba-handle" aria-hidden="true" />
        <label className="sr-only" htmlFor={inputId}>
          {copy.slider}
        </label>
        <input
          id={inputId}
          type="range"
          min={0}
          max={100}
          value={Math.round(pos)}
          tabIndex={active ? 0 : -1}
          onChange={(event) => {
            setSweeping(false);
            setPos(Number(event.target.value));
            onInteract();
          }}
          data-testid="hero-before-after-range"
        />
      </div>
      <p className="hero-ba-hint">{copy.hint}</p>
    </div>
  );
}
